import { GetApp } from "../../App"
import { UnitSelect } from "../../Components/UnitSelect"
import { IShipData } from "../../Types"
import { Block, BlockContainer, BlockField, BlockFieldInputs, BlockFieldInputsDivider, BlockRow } from "../../Components/Block"
import { EditGroup } from "../../Components/EditGroup"
import { Icon, Icons } from "../../Components/Icon"
import { NumberInput } from "../../Components/Input"
import { GetShipData, ShipDataViewData } from "./Context"
import { ShipDataEngineView } from "./EngineView"
import { ShipDataGeneratorView } from "./GeneratorView"
import { ShipDataSteeringSystemView } from "./SteeringSystemView"
import { ShipDataTankView } from "./TankView"
import { ShipDataTechnicViewProps } from "./Params"

export function ShipDataTechnicView(_: ShipDataTechnicViewProps) {
  const app = GetApp();
  const t = app.getT("ShipDataView");
  const { data, ship, editable, edited, onEdited, save, editEngine, editGenerator, editSteeringSystem, editTank } = GetShipData();
  return (
    <EditGroup changed={edited} editable={editable} editableFields={[

      { name: "engines", array: true, type: "object", fields: ["id", { name: "number", type: "int" }, { name: "nmeaNumber", type: "int", default: undefined }, "engineShortName", "manufacturer", "model", "serialNumber", { name: "power", type: "int" }, "powerUnit", "fuelType", "driveType", "propellerType"] },
      { name: "generators", array: true, type: "object", fields: ["id", "generatorType", "manufacturer", "model", "serialNumber", { name: "power", type: "int" }, "powerUnit", "fuelType", "comment"] },
      { name: "tanks", array: true, type: "object", fields: ["id", "tankType", { name: "capacity", type: "int" }, "capacityUnit", "placeInstallation", "comment"] },
      { name: "steeringSystems", array: true, type: "object", fields: ["id", "steeringType", "manufacturer", "model", { name: "power", type: "int" }, "powerUnit", "comment"] },
      "steeringSystem", { name: "rudderNumber", type: "int" }, "rudderShape", "rudderDrive",

      { name: "tankFuelNo", type: "int" }, { name: "tankWaterNo", type: "int" }, { name: "tankGrayNo", type: "int" }, { name: "tankWasteNo", type: "int" }, { name: "tankGasNo", type: "int" }, { name: "tankFuelSize", type: "int" }, "tankFuelSizeUnit", { name: "tankWaterSize", type: "int" }, "tankWaterSizeUnit", { name: "tankGraySize", type: "int" }, "tankGraySizeUnit", { name: "tankWasteSize", type: "int" }, "tankWasteSizeUnit", { name: "tankGasSize", type: "int" }, "tankGasSizeUnit",
    ]} onSave={save}
    >

      {/* Engines - ShipDataEngineView */}
      <BlockContainer>
        <Block>
          <BlockRow>
            <h3>{t("engines")}</h3>
          </BlockRow>

          {Array.from(Array(ship.engineCount).keys()).map((index) => {
            const engine = ShipDataViewData.engineAt({ engines: editEngine.items } as IShipData, index);
            return <ShipDataEngineView data={engine} editable={editable} index={index} key={engine.id} onEdited={onEdited} />;
          })}
        </Block>
      </BlockContainer>

      {/* Generators  - ShipDataGenView */}
      <BlockContainer>
        <Block>
          <BlockRow>
            <h3>{t("generators")}</h3>
          </BlockRow>

          {editGenerator.items.map((generator, index) => (
            <ShipDataGeneratorView data={generator} editable={editable} index={index} key={generator.id} onEdited={onEdited} removeGenerator={() => { editGenerator.removeAt(index); onEdited(); }} />
          ))}
          <BlockRow>
            <BlockFieldInputs>
              <button className="button" disabled={!editable} onClick={() => { editGenerator.add(); onEdited(); }}>
                <Icon icon={Icons.Plus} /> {t("add_generator")}
              </button>
            </BlockFieldInputs>
          </BlockRow>

        </Block>
      </BlockContainer>

      {/* SteeringSystem */}
      <BlockContainer>
        <Block>
          <BlockRow>
            <h3>{t("steering_systems")}</h3>
          </BlockRow>

          <BlockRow className="block-row-last main-steering-fields-row">
            <BlockField label={t("steering_system_main")} className="block_field_small_16">
              <input defaultValue={data.steeringSystem} name="steeringSystem" onChange={onEdited} readOnly={!editable} />
            </BlockField>
            <BlockField label={t("rudder_no")} className="block_field_small_8">
              <NumberInput format="int" defaultValue={data.rudderNumber} name="rudderNumber" onChange={onEdited} readOnly={!editable} />
            </BlockField>
            <BlockField label={t("rudder_shape")} className="block_field_small_12">
              <input defaultValue={data.rudderShape} name="rudderShape" onChange={onEdited} readOnly={!editable} />
            </BlockField>
            <BlockField label={t("rudder_drive")} className="block_field_small_16">
              <input defaultValue={data.rudderDrive} name="rudderDrive" onChange={onEdited} readOnly={!editable} />
            </BlockField>
          </BlockRow>

          {/* More SteeringSystems – ShipDataSteeringSystemView */}
          {editSteeringSystem.items.map((steeringSystem, index) => (
            <ShipDataSteeringSystemView data={steeringSystem} editable={editable} index={index} key={steeringSystem.id} onEdited={onEdited} removeSteeringSystem={() => { editSteeringSystem.removeAt(index); onEdited(); }} />
          ))}
          <BlockRow>
            <BlockFieldInputs className="button_top_distance">
              <button className="button" disabled={!editable} onClick={() => { editSteeringSystem.add(); onEdited(); }}>
                <Icon icon={Icons.Plus} /> {t("add_steeringsystem")}
              </button>
            </BlockFieldInputs>
          </BlockRow>

        </Block>
      </BlockContainer>

      {/* Tanks – ShipDataTankView */}
      <BlockContainer>
        <Block>
          <BlockRow>
            <h3>{t("tanks")}</h3>
          </BlockRow>

          <BlockRow className="tank-fields-row">
            <BlockField label={t("tank_fuel_no")} className="unit block_field_small_16">
              <BlockFieldInputs>
                <NumberInput format="int" defaultValue={data.tankFuelNo} name="tankFuelNo" onChange={onEdited} readOnly={!editable} className="block_field_small_3" />
                <BlockFieldInputsDivider />
                <NumberInput format="int" className="unit block_field_small_10" defaultValue={data.tankFuelSize} name="tankFuelSize" onChange={onEdited} readOnly={!editable} />
                <UnitSelect kind="volume" name="tankFuelSizeUnit" value={data.tankFuelSizeUnit} editable={editable} onChange={onEdited} className="unit-label block_field_small_3" />
              </BlockFieldInputs>
            </BlockField>
            <BlockField label={t("tank_water_no")} className="unit block_field_small_16">
              <BlockFieldInputs>
                <NumberInput format="int" defaultValue={data.tankWaterNo} name="tankWaterNo" onChange={onEdited} readOnly={!editable} className="block_field_small_3" />
                <BlockFieldInputsDivider />
                <NumberInput format="int" className="unit block_field_small_10" defaultValue={data.tankWaterSize} name="tankWaterSize" onChange={onEdited} readOnly={!editable} />
                <UnitSelect kind="volume" name="tankWaterSizeUnit" value={data.tankWaterSizeUnit} editable={editable} onChange={onEdited} className="unit-label block_field_small_3" />
              </BlockFieldInputs>
            </BlockField>
            <BlockField label={t("tank_gray_no")} className="unit block_field_small_16">
              <BlockFieldInputs>
                <NumberInput format="int" defaultValue={data.tankGrayNo} name="tankGrayNo" onChange={onEdited} readOnly={!editable} className="block_field_small_3" />
                <BlockFieldInputsDivider />
                <NumberInput format="int" className="unit block_field_small_10" defaultValue={data.tankGraySize} name="tankGraySize" onChange={onEdited} readOnly={!editable} />
                <UnitSelect kind="volume" name="tankGraySizeUnit" value={data.tankGraySizeUnit} editable={editable} onChange={onEdited} className="unit-label block_field_small_3" />
              </BlockFieldInputs>
            </BlockField>
          </BlockRow>
          <BlockRow className="block-row-last tank-fields-row">
            <BlockField label={t("tank_waste_no")} className="unit block_field_small_16">
              <BlockFieldInputs>
                <NumberInput format="int" defaultValue={data.tankWasteNo} name="tankWasteNo" onChange={onEdited} readOnly={!editable} className="block_field_small_3" />
                <BlockFieldInputsDivider />
                <NumberInput format="int" className="unit block_field_small_10" defaultValue={data.tankWasteSize} name="tankWasteSize" onChange={onEdited} readOnly={!editable} />
                <UnitSelect kind="volume" name="tankWasteSizeUnit" value={data.tankWasteSizeUnit} editable={editable} onChange={onEdited} className="unit-label block_field_small_3" />
              </BlockFieldInputs>
            </BlockField>
            <BlockField label={t("tank_gas_no")} className="unit block_field_small_16">
              <BlockFieldInputs>
                <NumberInput format="int" defaultValue={data.tankGasNo} name="tankGasNo" onChange={onEdited} readOnly={!editable} className="block_field_small_3" />
                <BlockFieldInputsDivider />
                <NumberInput format="int" className="unit block_field_small_10" defaultValue={data.tankGasSize} name="tankGasSize" onChange={onEdited} readOnly={!editable} />
                <UnitSelect kind="volume" name="tankGasSizeUnit" value={data.tankGasSizeUnit} editable={editable} onChange={onEdited} className="unit-label block_field_small_3" />
              </BlockFieldInputs>
            </BlockField>
          </BlockRow>

          {editTank.items.map((tank, index) => (
            <ShipDataTankView data={tank} editable={editable} index={index} key={tank.id} onEdited={onEdited} removeTank={() => { editTank.removeAt(index); onEdited(); }} />
          ))}
          <BlockRow>
            <BlockFieldInputs>
              <button className="button" disabled={!editable} onClick={() => { editTank.add(); onEdited(); }}>
                <Icon icon={Icons.Plus} /> {t("add_tank")}
              </button>
            </BlockFieldInputs>
          </BlockRow>
        </Block>
      </BlockContainer>

    </EditGroup>
  );
}
